// Управление подпиской клуба: продление кнопками, точная дата, приостановка.
//
// ОДИН компонент на две страницы («Клубы» → карточка клуба и «Лицензии»).
// Раньше продление жило только в «Лицензиях» и только как одобрение заявки с
// датой из календаря, а сам механизм продления был «выпустить новый ключ и
// переактивировать терминал» — именно он плодил устройства и дубли конфига.
//
// Продление в МЕСЯЦАХ, а не в днях: так же считаются счета (period_months) и
// авто-продление по оплате картой — одна арифметика во всём продукте
// (календарный месяц: 31.01 + 1 мес = 28.02).

import { useState } from 'react'
import { api } from '@/api/client'
import { useFetch, useMutate } from '@/hooks/useApi'
import { Button, Input, SectionHead } from '@/components/ui'
import { dateOnly, dateInputToMs, msToDateInput } from '@/lib/format'

/** Кнопки продления. Совпадают с периодами, на которые клуб выставляет счёт. */
const EXTEND_MONTHS = [1, 3, 6, 12] as const

export interface SubscriptionSummary {
  clubId: string
  expiresAt: number | null
  blockAtMs: number | null
  daysLeft: number | null
  blocked: boolean
  renewalStatus: string
  suspended: boolean
  devices: Array<{
    id: string
    name: string
    revoked: boolean
    lastSeenAt: number | null
    licenseExpiresAt: number | null
    machineId: string | null
  }>
}

/** Срок и остаток дней одной строкой — то, на что смотрят в первую очередь. */
function termLabel(s: SubscriptionSummary): React.JSX.Element {
  if (s.expiresAt == null) return <span className="muted">срок не задан</span>
  const days = s.daysLeft ?? 0
  const color = s.blocked ? 'var(--red)' : days <= 10 ? 'var(--amber)' : 'var(--green)'
  return (
    <span style={{ color }}>
      до {dateOnly(s.expiresAt)}
      {s.blocked ? ' — истекла' : ` · осталось ${days} дн.`}
    </span>
  )
}

export function SubscriptionActions({
  clubId,
  onChanged
}: {
  clubId: string
  /** Дёргается после любого изменения — чтобы страница перечитала свои списки. */
  onChanged?: () => void
}): React.JSX.Element {
  const sub = useFetch<SubscriptionSummary>(`/v1/clubs/${clubId}/subscription`, undefined, [clubId])
  const act = useMutate()
  const [customDate, setCustomDate] = useState('')
  const [confirmSuspend, setConfirmSuspend] = useState(false)

  const run = async (fn: () => Promise<unknown>): Promise<void> => {
    const ok = await act.mutate(fn)
    if (ok) {
      sub.reload()
      onChanged?.()
    }
  }

  const data = sub.data
  const activeDevices = data?.devices.filter((d) => !d.revoked).length ?? 0

  return (
    <div className="card" style={{ padding: '16px 18px', marginBottom: 18 }}>
      <SectionHead
        title="Подписка"
        meta={data ? termLabel(data) : sub.loading ? 'загрузка…' : undefined}
      />

      {sub.error && <div style={{ color: 'var(--red)', fontSize: 13 }}>{sub.error}</div>}

      {data && (
        <>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {EXTEND_MONTHS.map((m) => (
              <Button
                variant="primary"
                size="sm"
                key={m}
                disabled={act.saving}
                onClick={() =>
                  void run(() => api.post(`/v1/clubs/${clubId}/subscription/extend`, { months: m }))
                }
                title={`Продлить подписку на ${m} мес. от текущего срока`}
              >
                +{m} мес
              </Button>
            ))}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 8,
              marginTop: 14,
              flexWrap: 'wrap'
            }}
          >
            <label className="field" style={{ margin: 0 }}>
              <span className="field__label">Точная дата окончания</span>
              <Input
                type="date"
                value={customDate || (data.expiresAt ? msToDateInput(data.expiresAt) : '')}
                onChange={(e) => setCustomDate(e.target.value)}
              />
            </label>
            <Button
              variant="ghost"
              size="sm"
              disabled={act.saving || !customDate}
              onClick={() =>
                void run(() =>
                  api.put(`/v1/clubs/${clubId}/subscription/expires-at`, {
                    expiresAt: dateInputToMs(customDate)
                  })
                )
              }
            >
              Установить дату
            </Button>
          </div>

          <div
            style={{
              marginTop: 16,
              paddingTop: 14,
              borderTop: '1px solid var(--hairline-2)',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap'
            }}
          >
            <span className="muted" style={{ fontSize: 13, flex: 1 }}>
              {data.suspended
                ? 'Подписка приостановлена: терминалы отключены, срок сохранён.'
                : `Активных терминалов: ${activeDevices}. Приостановка отключает их сразу, не дожидаясь конца срока.`}
            </span>
            {data.suspended ? (
              <Button
                variant="primary"
                size="sm"
                disabled={act.saving}
                onClick={() =>
                  void run(() =>
                    api.post(`/v1/clubs/${clubId}/subscription/suspended`, { suspended: false })
                  )
                }
              >
                Возобновить
              </Button>
            ) : confirmSuspend ? (
              <>
                <Button
                  variant="danger"
                  size="sm"
                  disabled={act.saving}
                  onClick={() =>
                    void run(() =>
                      api.post(`/v1/clubs/${clubId}/subscription/suspended`, { suspended: true })
                    ).then(() => setConfirmSuspend(false))
                  }
                >
                  Да, приостановить
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setConfirmSuspend(false)}>
                  Не надо
                </Button>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                disabled={act.saving || activeDevices === 0}
                onClick={() => setConfirmSuspend(true)}
              >
                Приостановить
              </Button>
            )}
          </div>

          {act.error && (
            <div style={{ color: 'var(--red)', fontSize: 13, marginTop: 10 }}>{act.error}</div>
          )}
        </>
      )}
    </div>
  )
}
